import { createFeatureSelector, createSelector } from "@ngrx/store";
import { CurrentCallState, CallEntry, CallLeg, CallSession } from "./current-call-state";

/** Raw feature selector for the entire calls state */
export const selectCallsState =
  createFeatureSelector<CurrentCallState>("currentCall");

/** The active call entry (via activeCallId) — backward-compatible for consumers */
export const selectCurrentCall = createSelector(
  selectCallsState,
  (state): CallEntry | null => {
    if (!state.activeCallId) return null;
    return state.calls.find(c => c.callId === state.activeCallId) ?? null;
  }
);

/** All call entries */
export const selectAllCalls = createSelector(
  selectCallsState,
  (state): CallEntry[] => state.calls
);

/** Incoming calls (mainLeg status === 'incoming') */
export const selectIncomingCalls = createSelector(
  selectCallsState,
  (state): CallEntry[] => state.calls.filter(c => c.mainLeg?.status === 'incoming')
);

/** All active calls (any leg exists) */
export const selectActiveCalls = createSelector(
  selectCallsState,
  (state): CallEntry[] => state.calls.filter(c => c.mainLeg != null || c.pipLeg != null)
);

/** Factory selector: get a specific call by callId */
export const selectCallById = (callId: string | null) => createSelector(
  selectCallsState,
  (state): CallEntry | null => {
    if (!callId) return null;
    return state.calls.find(c => c.callId === callId) ?? null;
  }
);

/** Factory selector: find the first call whose destinationId matches */
export const selectCallForEntity = (entityId: string | null) => createSelector(
  selectCallsState,
  (state): CallEntry | null => {
    if (!entityId) return null;
    return state.calls.find(c => c.destinationId === entityId) ?? null;
  }
);

/** The active leg: pipLeg if it's speaking/outgoing, otherwise mainLeg */
export const selectActiveLeg = createSelector(
  selectCurrentCall,
  (state): CallLeg | null => {
    if (!state) return null;
    if (state.pipLeg && (state.pipLeg.status === 'speaking' || state.pipLeg.status === 'outgoing')) {
      return state.pipLeg;
    }
    return state.mainLeg ?? null;
  }
);

/** The held leg (mainLeg when on hold) */
export const selectHeldLeg = createSelector(
  selectCurrentCall,
  (state): CallLeg | null => {
    if (!state) return null;
    if (state.mainLeg?.status === 'hold') return state.mainLeg;
    return null;
  }
);

/** The active session from the active leg */
export const selectActiveSession = createSelector(
  selectActiveLeg,
  (leg): CallSession | null => {
    if (!leg) return null;
    return leg.sessions.find(s => s.status === 'answered') ?? leg.sessions[0] ?? null;
  }
);

/** Whether a forward is in progress (mainLeg on hold + pipLeg exists) */
export const selectIsForwarding = createSelector(
  selectCurrentCall,
  (state): boolean => {
    if (!state) return false;
    return state.mainLeg?.status === 'hold' && state.pipLeg != null;
  }
);

/** Whether the active leg entity supports V2 call protocol */
export const selectActiveLegIsV2 = createSelector(
  selectActiveLeg,
  (leg): boolean => leg?.entityIsV2 ?? false
);
